/* eslint-disable
    import-x/order
*/
// Fix any style issues and re-enable lint.
import DataConnector from 'connect/DataConnector';
import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {schemaFieldToColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {PLACEHOLDER_CELL} from 'notebook/tables/constants/CellConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import getOrFetchResultValidationInConnector, {
  TableAssayValidationEnhancedProps,
} from 'results/validation/getOrFetchResultValidationInConnector';
import getTableWithValidationIndicators from 'results/validation/getTableWithValidationIndicators';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {PROXIED_NOTE_PROP_KEYS, ProxiedNoteProps} from 'notebook/util/NoteUtilConstants';

import {AssayResult, OVERSIZED_RESULTS_TABLE_TOOLTIP} from 'results/assays/AssayRecordConstants';
import {FixedColumn} from './ColumnConstants';
import {Cells} from 'notebook/tables/constants/RowConstants';
import {TableHeaderControlsCreator} from 'notebook/tables/constants/TableConstants';

export interface FixedColumnAssayResultTableProps extends Omit<ProxiedNoteProps, 'locked'> {
  autoCollapseLimit?: number;
  columns: Array<FixedColumn>;
  results: Array<AssayResult>;
  schema: AssaySchema;
  tableName: string;
  canRename: boolean;
  isOversized?: boolean;
  forceRowCount?: number;
  headerControls?: TableHeaderControlsCreator;
  leftHeaderControls?: JSX.Element;
  dataTestAttrs?: DataTestAttrs;
}

export class FixedColumnAssayResultTable extends React.Component<
  FixedColumnAssayResultTableProps & TableAssayValidationEnhancedProps
> {
  _getTableRows(): Cells {
    // TableEditable expects at least 1 row
    if (this.props.results.length === 0) {
      return [new Array(this.props.columns.length).fill(PLACEHOLDER_CELL)];
    }

    return this.props.results.map((result) => this.props.columns.map((column) => column.render(result)));
  }

  _getAffixedColumnCount(): number {
    let count = 0;
    for (const column of this.props.columns) {
      if (!column.shouldAffix) {
        break;
      }
      count++;
    }
    assertAndContinue(
      this.props.columns.slice(count).every((column) => !column.shouldAffix),
      'Unable to affix column after a non-affixed column'
    );
    return count;
  }

  _getColumnConstraints(): Array<ColumnConstraint> {
    return this.props.columns.map((column) => {
      const columnConstraint: ColumnConstraint = {renderedLabel: column.label};
      if (column.schemaField) {
        columnConstraint.columnDefinition = {
          ...schemaFieldToColumnDefinition(column.schemaField),
          skipSchemaValidation: true,
        };
      }
      return columnConstraint;
    });
  }

  override render(): JSX.Element {
    return (
      <SimpleTableEditable
        {...standaloneProps}
        // eslint-disable-next-line no-restricted-syntax
        {..._.pick(this.props, PROXIED_NOTE_PROP_KEYS)}
        autoCollapseLimit={this.props.autoCollapseLimit}
        locked={true}
        table={getTableWithValidationIndicators(
          {
            // eslint-disable-next-line no-restricted-syntax
            widths: _.pluck(this.props.columns, 'width'),
            table: this._getTableRows(),
            allowColumnEdits: false,
            headerCol: false,
            headerRow: false,
            lockedDims: true,
            // Pass props.tableName as tableLabel, so that it's rendered as a black label instead of gray name
            name: '',
            columnLabels: [],
            nameTooltip: this.props.isOversized ? OVERSIZED_RESULTS_TABLE_TOOLTIP : null,
            columnConstraints: this._getColumnConstraints(),
            rowData: this.props.results.map((result) => ({
              euid: result.id,
              uiData: {archivePurpose: result.archiveRecord?.purpose},
            })),
            defaultAffixedColumnCount: this._getAffixedColumnCount(),
            _hideRename: !this.props.canRename,
          },
          {
            assayValidationByResultId: this.props.assayValidationByResultId,
            fieldHasValueDictByResultId: this.props.fieldHasValueDictByResultId,
            assayResultIdsByRow: this.props.results.map((result) => result.id),
            assaySchema: this.props.schema,
            // This table does not do any server-side validation
            dataTableErrorsByRecordId: {},
          }
        )}
        forceCollapsed={this.props.isOversized}
        forceRowCount={this.props.forceRowCount}
        headerControls={this.props.headerControls}
        leftHeaderControls={({isCollapsed}) => isCollapsed && this.props.leftHeaderControls}
        isCollapsible={true}
        hideFormulaBox={true}
        editWhileTableLockedWarning={null}
        getEditWhileCellLockedWarning={null}
        tableLabel={this.props.tableName}
        dataTestAttrs={this.props.dataTestAttrs}
      />
    );
  }
}

class FixedColumnAssayResultTableConnector extends DataConnector<
  FixedColumnAssayResultTableProps,
  TableAssayValidationEnhancedProps
> {
  override reduxSubscriptionPaths = ['models.results.assayResultsById'];
  enhanceProps(): TableAssayValidationEnhancedProps {
    return getOrFetchResultValidationInConnector(
      this,
      this.props.results.map((result) => result.id)
    );
  }
}

export default FixedColumnAssayResultTableConnector.connect(FixedColumnAssayResultTable);
